import { Dayjs } from "dayjs"
import {
  checkupReusableVitals,
  vitals,
  vitalsNeededByReason,
  vitalsNeededForCheckup,
} from "./services/constants"
import { vitalsType } from "types/payload"
import { CheckupVitals } from "types/store"
import { age, toDayjs } from "lib/utils"

export type VitalNeeded = { label: vitalsType; alwaysNeeded?: boolean }

const ADULT_AGE = 18

// A child's weight changes quickly, so it is asked at every consultation
// instead of reusing the last recorded value. An unknown or invalid
// birthdate keeps the adult behaviour.
const isMinor = (birthdate?: string | Dayjs): boolean => {
  if (!birthdate) return false
  const datejs = toDayjs(birthdate)
  if (!datejs?.isValid()) return false
  return age(datejs) < ADULT_AGE
}

const getVitalsNeededByReason = (
  reason: string,
  patientBirthdate?: string | Dayjs,
): Array<VitalNeeded> => {
  const vitalsNeeded: Array<VitalNeeded> =
    reason === "CHECKUP"
      ? vitalsNeededForCheckup
      : vitalsNeededByReason?.[reason] || []
  if (!isMinor(patientBirthdate)) {
    return vitalsNeeded
  }
  return vitalsNeeded.map((vital) =>
    vital.label === vitals.WEIGHT ? { ...vital, alwaysNeeded: true } : vital,
  )
}

const isMeasure = (raw: unknown): boolean => {
  if (raw === undefined || raw === null || raw === "") return false
  const value = Number(String(raw).replace(",", "."))
  return Number.isFinite(value) && value > 0
}

// A vital counts as recorded only with an actual measure: blood pressure
// needs both its systolic and diastolic values
const hasRecordedMeasure = (label: string, recorded: any): boolean => {
  const value = recorded?.value
  if (label === vitals.TENSION) {
    return isMeasure(value?.systolic?.raw) && isMeasure(value?.diastolic?.raw)
  }
  return isMeasure(value?.raw)
}

// Vitals measured during the check-up that a following teleconsultation can
// reuse instead of asking them again. A skipped vital has no measure and is
// left out, so it is asked again.
const getCheckupVitalsToReuse = (
  patientVitals: Record<string, any> | undefined,
): string[] =>
  checkupReusableVitals.filter((label) =>
    hasRecordedMeasure(label, patientVitals?.[label]),
  )

// The list only depends on the check-up marker, which is set once when the
// teleconsultation is launched: a vital entered during the teleconsultation
// does not shift the remaining steps.
const getVitalsToAsk = (
  vitalsNeeded: Array<VitalNeeded>,
  checkupVitals: CheckupVitals | undefined,
  patientId: number | undefined,
): Array<VitalNeeded> => {
  if (patientId === undefined || checkupVitals?.patientId !== patientId) {
    return vitalsNeeded
  }
  return vitalsNeeded.filter((v) => !checkupVitals.types.includes(v.label))
}

export { getVitalsNeededByReason, getCheckupVitalsToReuse, getVitalsToAsk }
